import { useEffect } from 'react';
import { Checkbox } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import {
    setIsOneClickBettingEnabled,
    setOneClickBettingStake,
    setCustomStakes,
} from '../../store/exchBetSlip/exchBetSlipReducer';
import { NumericFormat } from 'react-number-format';
import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../models/RootState';
import { CheckBoxOutlined } from '@mui/icons-material';

const OneClickBetting = ({ lobby }: { lobby: string }) => {
    const dispatch = useDispatch();
    const { isOneClickBettingEnabled, oneClickBettingStake, customStakes } = useSelector(
        (state: RootState) => state.exchBetSlip
    );
    const [isEditMode, setIsEditMode] = useState(false);
    const [stakesInput, setStakesInput] = useState<number[]>(customStakes);

    useEffect(() => {
        setStakesInput(customStakes);
        if (customStakes?.length > 0 && oneClickBettingStake === 0) {
            dispatch(setOneClickBettingStake(customStakes[0]));
        }
    }, [customStakes, oneClickBettingStake, dispatch]);

    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    return (
        <div className="m-1">
            {/* Main Container - Single cohesive unit */}
            <div className={`${lobby === "SPORTSBOOK" ? "bg-[var(--sb-mkt-name-bg)]" : "bg-[var(--table-bg)]"} rounded-lg p-1 flex flex-col gap-1 transition-opacity duration-300 opacity-100`}>
                {/* Top Section - Enable/Disable and Edit */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center text-xs text-gray-200 font-medium">
                        <Checkbox
                            color="primary"
                            style={{
                                color: 'var(--ion-color-primary)',
                                padding: '0px',
                                transform: 'scale(0.7)',
                            }}
                            onClick={() => {
                                dispatch(setIsOneClickBettingEnabled(!isOneClickBettingEnabled));
                                setIsEditMode(false);
                            }}
                            checked={isOneClickBettingEnabled}
                            className="pr-1"
                            size="small"
                        />
                        1 Click Betting Enabled
                    </div>
                    {!isEditMode ? (
                        <EditIcon
                            onClick={() => {
                                setIsEditMode(true);
                                if (!isEditMode) {
                                    dispatch(setOneClickBettingStake(0));
                                }
                            }}
                            className="cursor-pointer text-gray-200 text-lg"
                            style={{ fontSize: '18px' }}
                        />
                    ) : (
                        <CheckBoxOutlined
                            onClick={() => {
                                setIsEditMode(false);
                                // Save custom stakes to Redux
                                dispatch(setCustomStakes(stakesInput));
                                // Set the first stake as selected
                                dispatch(setOneClickBettingStake(stakesInput[0]));
                            }}
                            className="cursor-pointer text-[var(--ion-color-primary)] text-lg"
                            style={{ fontSize: '18px' }}
                        />
                    )}
                </div>

                {/* Bottom Section - Stake Buttons */}
                {isOneClickBettingEnabled && (
                    <div className="flex items-center gap-1">
                        {stakesInput?.map((stake, index) => (
                            <div
                                key={`stake-${index}`}
                                onClick={(e) => {
                                    if (!isEditMode) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        dispatch(setOneClickBettingStake(stake || 0));
                                    }
                                }}
                                className={`flex-1 ${oneClickBettingStake === stake ? 'selected' : ''} ${!isEditMode ? 'cursor-pointer' : 'cursor-default'}`}
                            >
                                <NumericFormat
                                    thousandSeparator={true}
                                    name="stake"
                                    value={stake}
                                    disabled={!isEditMode}
                                    getInputRef={(el: any) => {
                                        inputRefs.current[index] = el;
                                    }}
                                    onValueChange={(values) => {
                                        if (isEditMode) {
                                            const newValue = values.floatValue || null;
                                            const newStakes = [...stakesInput];
                                            newStakes[index] = newValue || 0;
                                            setStakesInput(newStakes);
                                            // Maintain focus after state update
                                            setTimeout(() => {
                                                if (inputRefs.current[index]) {
                                                    inputRefs.current[index]?.focus();
                                                }
                                            }, 0);
                                        }
                                    }}
                                    className={`w-full text-center font-bold text-xs py-1 px-1 rounded border border-[var(--ion-color-primary)] transition-all duration-200 ${isEditMode
                                        ? 'bg-white text-gray-800 cursor-text pointer-events-auto'
                                        : oneClickBettingStake === stake
                                            ? 'bg-[var(--ion-color-primary)] text-white cursor-pointer pointer-events-none'
                                            : 'bg-white text-gray-800 cursor-pointer pointer-events-none'
                                        }`}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default OneClickBetting;